import * as React from "react";

interface PinInputProps {
  length: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  size?: "sm" | "default" | "lg"; // sm: 40×40 (six slots on a phone), default: 48×48, lg: 56×56
  /** Hide each digit behind a bullet. On for PINs; off for one-time codes read off a text. */
  mask?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  "data-testid"?: string;
}

export function PinInput({
  length,
  value,
  onChange,
  onComplete,
  size = "default",
  mask = true,
  autoFocus = false,
  disabled = false,
  "data-testid": testId,
}: PinInputProps) {
  const inputRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const dim = size === "lg" ? 56 : size === "sm" ? 40 : 48;

  // Ensure the refs array is the right length
  if (inputRefs.current.length !== length) {
    inputRefs.current = Array(length).fill(null);
  }

  // Auto-focus on mount if requested
  React.useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  function setDigit(index: number, digit: string) {
    const chars = value.split("").concat(Array(length).fill("")).slice(0, length);
    chars[index] = digit;
    const next = chars.join("");
    onChange(next);
    return next;
  }

  // Several digits arriving in one box at once: the keyboard's "From Messages"
  // one-time-code suggestion, a password manager, dictation. A typed character
  // never takes this path — it arrives alone, or as "<existing><new>".
  function fillFrom(index: number, digits: string) {
    // A whole code belongs at the first box, wherever the cursor happened to be.
    const start = digits.length >= length ? 0 : index;
    const chars = value.split("").concat(Array(length).fill("")).slice(0, length);
    for (let i = 0; i < digits.length && start + i < length; i++) {
      chars[start + i] = digits[i];
    }
    const next = chars.join("");
    onChange(next);
    inputRefs.current[Math.min(start + digits.length, length - 1)]?.focus();
    if (next.length === length) {
      onComplete?.(next);
    }
  }

  function handleInput(index: number, e: React.FormEvent<HTMLInputElement>) {
    const input = e.currentTarget;
    const digits = input.value.replace(/\D/g, "");
    if (!digits) {
      // Non-numeric — clear the native input value and do nothing
      input.value = "";
      return;
    }
    const existing = value[index] ?? "";
    // One character typed into a box that already holds one: the box now reads
    // "<existing><new>", or "<new><existing>" if the cursor sat in front.
    const typedOverExisting =
      digits.length === 2 && existing !== "" && (digits.startsWith(existing) || digits.endsWith(existing));
    if (digits.length > 1 && !typedOverExisting) {
      fillFrom(index, digits);
      return;
    }
    const typed = digits.length === 1 ? digits : digits.startsWith(existing) ? digits[1] : digits[0];
    const next = setDigit(index, typed);
    // Advance focus
    if (index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    // Every box filled. (This used to also test `!next.includes("")`, which is
    // false for every string, so onComplete never fired for typed input.)
    if (next.length === length) {
      onComplete?.(next);
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      const currentChar = value[index] ?? "";
      if (currentChar) {
        // Clear current slot
        setDigit(index, "");
      } else if (index > 0) {
        // Move to previous slot and clear it
        inputRefs.current[index - 1]?.focus();
        setDigit(index - 1, "");
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      e.preventDefault();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    const digits = text.replace(/\D/g, "").slice(0, length);
    if (!digits) return;
    const padded = digits.padEnd(length, "").slice(0, length);
    // Build the new value: fill from clipboard, keep remainder from existing value
    const existing = value.padEnd(length, "").slice(0, length).split("");
    const next = existing
      .map((ch, i) => (i < digits.length ? digits[i] : ch))
      .join("");
    onChange(next);
    // Focus the slot after the last pasted digit
    const focusIdx = Math.min(digits.length, length - 1);
    inputRefs.current[focusIdx]?.focus();
    if (next.replace(/\s/g, "").length === length) {
      onComplete?.(next);
    }
  }

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    // Select any existing value so typing replaces it
    e.currentTarget.select();
  }

  return (
    <div
      data-testid={testId}
      style={{ display: "flex", flexDirection: "row", gap: size === "sm" ? 6 : 8, alignItems: "center", justifyContent: "center" }}
    >
      {Array.from({ length }, (_, i) => {
        const char = value[i] ?? "";
        const isFilled = char !== "";
        return (
          <div
            key={i}
            style={{
              position: "relative",
              width: dim,
              height: dim,
              minWidth: dim,
              maxWidth: dim,
              flexShrink: 0,
            }}
          >
            <input
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              // Room for the character typed after an existing one, and for a
              // whole code filled in at once (see fillFrom).
              maxLength={Math.max(2, length)}
              autoComplete="one-time-code"
              disabled={disabled}
              onInput={(e) => handleInput(i, e)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              onFocus={handleFocus}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                fontSize: 16, // ≥16px prevents iOS auto-zoom
                textAlign: "center",
                border: "2px solid",
                borderRadius: 8,
                outline: "none",
                cursor: "text",
                // Color transparent so the ● overlay is what the user sees
                color: "transparent",
                caretColor: "#2B5397",
                backgroundColor: isFilled ? "#ffffff" : "#f3f4f6",
                borderColor: isFilled ? "#2B5397" : "#d1d5db",
                boxSizing: "border-box",
                WebkitTapHighlightColor: "transparent",
                // Active state handled via CSS :focus below; inline styles take precedence
              }}
              className="pin-input-slot"
              // Store the digit as the input value so the browser sees the field as "filled"
              value={char}
              onChange={() => {
                // Controlled — actual updates happen in onInput/onKeyDown
              }}
            />
            {/* Bullet (or, unmasked, digit) overlay */}
            {isFilled && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: mask ? 24 : 22,
                  fontWeight: mask ? undefined : 600,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1,
                  color: "#111827",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              >
                {mask ? "●" : char}
              </span>
            )}
          </div>
        );
      })}
      <style>{`
        .pin-input-slot:focus {
          border-color: #2B5397 !important;
          box-shadow: 0 0 0 2px #2B5397 !important;
          background-color: #ffffff !important;
          outline: none;
        }
      `}</style>
    </div>
  );
}
